import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { getItemToken } from "../../../Utils/localStorageUtils";

/**
 * LiveStreamMonitor — compact per-stream Health + Concurrent viewers badge with
 * an on-hover analytics panel. Consumes GET /admin/livestream/:id/monitor.
 *
 * Self-contained: drop <LiveStreamMonitor streamId={row.id} /> into any cell.
 * Polls every 15s so the live count stays fresh without a page refresh.
 */
const POLL_MS = 15000;

const Row = ({ label, value }) => (
  <div className="d-flex justify-content-between" style={{ gap: 16 }}>
    <span style={{ opacity: 0.75 }}>{label}</span>
    <b>{value}</b>
  </div>
);

const LiveStreamMonitor = ({ streamId }) => {
  const [data, setData] = useState(null);
  const [errored, setErrored] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!streamId) return undefined;
    const baseUrl = (process.env.REACT_APP_Baseurl || "").replace(/\/$/, "");
    const headers = getItemToken("access_token");
    let active = true;

    const load = async () => {
      try {
        const { data: res } = await axios.get(
          `${baseUrl}/admin/livestream/${streamId}/monitor`,
          { headers }
        );
        if (active && res?.status) {
          setData(res.data);
          setErrored(false);
        }
      } catch (e) {
        if (active) setErrored(true);
      }
    };

    load();
    timer.current = setInterval(load, POLL_MS);
    return () => {
      active = false;
      if (timer.current) clearInterval(timer.current);
    };
  }, [streamId]);

  if (errored) return <span className="dateText theme-text-color">—</span>;
  if (!data) return <span className="dateText theme-text-color">…</span>;

  const { health = {}, viewers = {}, analytics = {}, audience = {} } = data;
  const label = health.status_label || "OFFLINE";
  const color =
    label === "LIVE" ? "#16a34a" : label.startsWith("ON-AIR") ? "#d97706" : "#9ca3af";

  return (
    <div
      style={{ position: "relative", minWidth: 118 }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <div
        className="d-flex align-items-center theme-text-color"
        style={{ fontSize: 13, cursor: "default" }}
      >
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: color,
            marginRight: 6,
            boxShadow: label === "LIVE" ? `0 0 0 3px ${color}33` : "none",
          }}
        />
        <span style={{ fontWeight: 600, color }}>{label}</span>
        <span className="ms-2" title="Watching now">
          &#128065; {viewers.current ?? 0}
        </span>
      </div>

      {open && (
        <div
          className="theme-bg-color shadow"
          style={{
            position: "absolute",
            zIndex: 40,
            top: "100%",
            left: 0,
            marginTop: 6,
            padding: "10px 12px",
            borderRadius: 8,
            minWidth: 220,
            border: "1px solid #00000022",
            fontSize: 12,
          }}
        >
          <Row label="Watching now" value={viewers.current ?? 0} />
          <Row label="Peak today" value={viewers.peak_today ?? 0} />
          <hr style={{ margin: "6px 0", opacity: 0.2 }} />
          <Row label="Total sessions" value={analytics.total_sessions ?? 0} />
          <Row label="Unique viewers" value={analytics.unique_viewers ?? 0} />
          <Row label="Watch time" value={`${analytics.total_watch_minutes ?? 0} min`} />
          <Row label="Avg completion" value={`${analytics.avg_completion_pct ?? 0}%`} />
          {Array.isArray(audience.by_country) && audience.by_country.length > 0 && (
            <>
              <hr style={{ margin: "6px 0", opacity: 0.2 }} />
              <div className="mb-1" style={{ opacity: 0.7 }}>
                Top regions
              </div>
              {audience.by_country.slice(0, 3).map((c, i) => (
                <Row key={i} label={c.country} value={c.viewers} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LiveStreamMonitor;
